import React, { useState } from 'react';
import { cn } from '../utils/cn';

const TabsContext = React.createContext({});

export const Tabs = ({ defaultValue, value, onValueChange, className, children, ...props }) => {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const currentValue = value !== undefined ? value : internalValue;

  const handleValueChange = (newValue) => {
    if (value === undefined) {
      setInternalValue(newValue);
    }
    if (onValueChange) {
      onValueChange(newValue);
    }
  };

  return (
    <TabsContext.Provider value={{ value: currentValue, onValueChange: handleValueChange }}>
      <div className={cn("w-full", className)} {...props}>
        {React.Children.map(children, child => {
          if (child.type === TabsList) {
            return React.cloneElement(child, {
              value: currentValue,
              onValueChange: handleValueChange
            });
          }
          return child;
        })}
      </div>
    </TabsContext.Provider>
  );
};

export const TabsList = ({ className, children, value, onValueChange, ...props }) => (
  <div
    className={cn(
      "inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground",
      className
    )}
    {...props}
  >
    {children}
  </div>
);

export const TabsTrigger = ({ value, className, children, ...props }) => {
  const context = React.useContext(TabsContext);
  const isActive = context.value === value;

  return (
    <button
      className={cn(
        "inline-flex items-center max-lg:text-xs bg-white justify-center cursor-pointer whitespace-nowrap outline-none rounded-full px-3 py-1.5 text-xs font-medium ring-offset-background transition-all disabled:pointer-events-none disabled:opacity-50",
        isActive ? "bg-background text-foreground shadow-sm shadow-[var(--color-primary-500)] text-[var(--color-primary-500)]" : "text-gray-700 hover:bg-background/50 hover:text-foreground",
        className
      )}
      onClick={() => context.onValueChange(value)}
      {...props}
    >
      {children}
    </button>
  );
};

export const TabsContent = ({ value, className, children, ...props }) => {
  const context = React.useContext(TabsContext);
  
  if (context.value !== value) return null;
  
  return (
    <div
      className={cn(
        "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};